import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Holiday } from '@keetcheck/shared';
import { theme } from '../../theme.js';
import { useAuth } from '../../context/AuthContext.js';
import { api } from '../../lib/api.js';
import {
  useSchedules, useTeams, useOrgMembers, useDutyRoster,
  useSetDefaultDayUdo, useFillDutyRoster, useAddHoliday, useRemoveHoliday,
} from '../../lib/queries.js';
import { RosterGrid } from '../../components/RosterGrid.js';
import { Icon } from '../../components/Icon.js';
import { currentDutyMonday, shortDate, localDay } from '../../lib/shifts.js';
import { ScratchWindowsCard } from '../ScratchPages.js';

/**
 * The unit roster: who's on duty, ops support and UDO each week, plus the
 * weekly check assignments, in one grid like the published sheet. Shifts are
 * generated from it 8 weeks ahead.
 */
export function RosterPage() {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const { data: schedules = [] } = useSchedules(orgId);
  const { data: teams = [] } = useTeams(orgId);
  const { data: members = [] } = useOrgMembers(orgId);
  const { data: roster } = useDutyRoster(orgId, 12);
  const weekly = schedules.filter((s) => s.active && s.cadence === 'weekly');
  const [scheduleId, setScheduleId] = useState<string | null>(null);
  const schedule = weekly.find((s) => s.id === scheduleId) ?? weekly[0] ?? null;

  return (
    <div style={{ flex: 1, background: theme.surface, padding: '12px 16px 32px', fontFamily: theme.font }}>
      <SettingsCard orgId={orgId} defaultDayUdoId={roster?.defaultDayUdoId ?? null} members={members} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, margin: '16px 0 6px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: theme.textDim }}>
          Roster <span style={{ fontWeight: 500, color: theme.textMuted }}>· tap a week to change it</span>
        </div>
        {weekly.length > 1 && (
          <select value={schedule?.id ?? ''} onChange={(e) => setScheduleId(e.target.value)} aria-label="Check schedule" style={select}>
            {weekly.map((s) => <option key={s.id} value={s.id}>Checks: {s.name}</option>)}
          </select>
        )}
      </div>
      <RosterGrid orgId={orgId} schedule={schedule} teams={teams} members={members} />

      <ScratchWindowsCard orgId={orgId} />
      <HolidaysCard orgId={orgId} />
    </div>
  );
}

function SettingsCard({ orgId, defaultDayUdoId, members }: {
  orgId: string; defaultDayUdoId: string | null; members: { userId: string; displayName: string; udo: boolean }[];
}) {
  const setDefault = useSetDefaultDayUdo(orgId);
  const fill = useFillDutyRoster(orgId);
  const [filled, setFilled] = useState<number | null>(null);
  const udos = members.filter((m) => m.udo);

  return (
    <div style={card}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: theme.text, flexWrap: 'wrap' }}>
        <span style={{ flex: '1 1 180px' }}>
          <strong>Default day UDO</strong>
          <div style={{ fontSize: 12, color: theme.textMuted }}>Weekdays 6am–6pm, unless a week names someone else.</div>
        </span>
        <select
          value={defaultDayUdoId ?? ''} disabled={setDefault.isPending}
          onChange={(e) => setDefault.mutate(e.target.value || null)}
          aria-label="Default day UDO" style={{ ...select, minWidth: 160 }}
        >
          <option value="">— none</option>
          {udos.map((m) => <option key={m.userId} value={m.userId}>{m.displayName}</option>)}
        </select>
      </label>
      <div style={{ borderTop: `1px solid ${theme.border}`, margin: '12px 0' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ flex: '1 1 180px', fontSize: 12, color: theme.textMuted }}>
          Continue the Duty / Ops Support crews' cycle into weeks that aren't rostered yet (UDOs stay blank).
        </span>
        <button
          disabled={fill.isPending}
          onClick={() => fill.mutate({ from: currentDutyMonday(), weeks: 12 }, { onSuccess: (r) => setFilled(r.filled.length) })}
          style={btn}
        >
          {fill.isPending ? 'Filling…' : 'Fill next 12 weeks'}
        </button>
      </div>
      {filled !== null && (
        <div style={{ fontSize: 12, color: theme.textDim, marginTop: 6 }}>
          {filled === 0 ? 'Nothing to fill — those weeks are already rostered (or there is no pattern to follow yet).' : `Filled ${filled} week${filled > 1 ? 's' : ''}. Check them and pick the UDOs.`}
        </div>
      )}
    </div>
  );
}

function HolidaysCard({ orgId }: { orgId: string }) {
  const { data: holidays = [] } = useQuery({
    queryKey: ['orgs', orgId, 'duty-roster', 'holidays'],
    queryFn: () => api.get<Holiday[]>(`/orgs/${orgId}/holidays`),
    enabled: !!orgId,
  });
  const add = useAddHoliday(orgId);
  const remove = useRemoveHoliday(orgId);
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const [showPast, setShowPast] = useState(false);
  const today = localDay(new Date().toISOString());
  const shown = holidays.filter((h) => showPast || h.date >= today);

  return (
    <div style={{ ...card, marginTop: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>Public holidays</div>
      <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 8 }}>
        On a weekday holiday the duty crew, UDO and ops support also cover 6am–6pm. A Monday holiday belongs to the week before (the long weekend).
      </div>
      {shown.map((h) => (
        <div key={h.date} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: `1px solid ${theme.border}` }}>
          <span style={{ width: 92, fontSize: 13, fontWeight: 600, color: theme.text }}>
            {new Date(`${h.date}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
          </span>
          <span style={{ flex: 1, fontSize: 13, color: theme.textDim }}>{h.name}</span>
          <span style={{ fontSize: 11, color: theme.textMuted }}>{h.date.slice(0, 4)}</span>
          <button onClick={() => remove.mutate(h.date)} disabled={remove.isPending} aria-label={`Remove ${h.name}`} style={iconBtn}>
            <Icon name="x" size={12} />
          </button>
        </div>
      ))}
      <button onClick={() => setShowPast(!showPast)} style={{ ...linkBtn, marginTop: 4 }}>
        {showPast ? 'Hide past holidays' : 'Show past holidays'}
      </button>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate({ date, name: name.trim() }, { onSuccess: () => { setDate(''); setName(''); } });
        }}
        style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}
      >
        <input type="date" required value={date} onChange={(e) => setDate(e.target.value)} aria-label="Holiday date" style={{ ...select, width: 150 }} />
        <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Name, e.g. Local show day"
          aria-label="Holiday name" maxLength={100} style={{ ...select, flex: '1 1 160px' }} />
        <button type="submit" disabled={add.isPending} style={btn}>Add</button>
      </form>
      {holidays.length > 0 && (
        <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 8 }}>
          Pre-loaded with Victorian public holidays to {shortDate(holidays[holidays.length - 1]!.date)} {holidays[holidays.length - 1]!.date.slice(0, 4)}.
        </div>
      )}
    </div>
  );
}

const card: React.CSSProperties = {
  padding: '12px 14px', borderRadius: 12, background: theme.surface2, border: `1px solid ${theme.border}`,
};

const select: React.CSSProperties = {
  padding: '7px 9px', fontSize: 13, borderRadius: 8, border: `1px solid ${theme.border}`,
  background: theme.surface, color: theme.text, fontFamily: theme.font, boxSizing: 'border-box', minWidth: 0,
};

const btn: React.CSSProperties = {
  padding: '7px 12px', fontSize: 12, fontWeight: 700, borderRadius: 8, cursor: 'pointer',
  border: `1px solid ${theme.border}`, background: theme.surface, color: theme.text, fontFamily: theme.font,
};

const iconBtn: React.CSSProperties = {
  width: 24, height: 24, borderRadius: 6, border: `1px solid ${theme.border}`, background: theme.surface,
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textMuted, flexShrink: 0,
};

const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', padding: 0, color: theme.accent, fontWeight: 700, fontSize: 12,
  cursor: 'pointer', fontFamily: theme.font,
};
